import {ConfigType} from 'coreComponents/Carousel/ICarousel';
import {stringToHash} from 'coreUtils/utils';

import {
	IGenerateBreakpointsStylesParams,
	IProcessStylesConfig,
	ISsrStyleEntry,
	IStylesFunctions
} from './ICarouselSSRStyle';

const transformModifiers = new Set([
	'generateSlidesOffsetStyle',
	'generateCenteredSlidesStyle',
	'generateCoverflowEffectSlideStyle'
]);

/*
	object to save value of transform rule and be able to merge it when necessary
	E.g. {cr-1265817770 : 'translate3d(x, y, z)'}
 */
const transformObjects: Record<string, string> = {};

const stylesFunctions: IStylesFunctions = {
	generateSlidesPerViewStyle: (config: Partial<ConfigType> = {}): string => {
		const {slidesPerView, spaceBetween, slidesOffsetBefore = 0, slidesOffsetAfter = 0} = config;

		if (typeof slidesPerView !== 'number') {
			return;
		}

		let width = `100%`;
		const percentage = 100 / slidesPerView;

		const fixedSpacing = (spaceBetween || 0) * (slidesPerView - 1) + slidesOffsetBefore + slidesOffsetAfter;

		if (slidesPerView && fixedSpacing) {
			width = `calc((100% - ${fixedSpacing}px) / ${slidesPerView})`;
		} else if (slidesPerView) {
			width = `${percentage}%`;
		}

		return `width: ${width};`;
	},
	generateSpaceBetweenStyle: (config: Partial<ConfigType> = {}): string => {
		const {spaceBetween} = config;

		if (!(typeof spaceBetween === 'number')) {
			return;
		}

		return `margin-inline-end: ${spaceBetween}px;`;
	},
	generateSlidesOffsetStyle: (config: Partial<ConfigType> = {}): string => {
		const {slidesOffsetBefore} = config;

		if (!(typeof slidesOffsetBefore === 'number')) {
			return;
		}

		return `transform: translateX(${slidesOffsetBefore}px);`;
	},
	generateCenteredSlidesStyle: (config: Partial<ConfigType> = {}): string => {
		const {centeredSlides, slidesPerView} = config;

		if (!centeredSlides || !(typeof slidesPerView === 'number')) {
			return;
		}

		return `transform: translateX(calc((100% - calc(100% / ${slidesPerView})) / 2));`;
	},
	generateCoverflowEffectSlideStyle: (config: Partial<ConfigType> = {}): string => {
		const {coverflowEffectConfig} = config;
		const depth = coverflowEffectConfig?.depth;

		if (depth === undefined || depth === null) {
			return;
		}

		return `transform: translateZ(-${depth}px);`;
	},
	generateBreakpointsStyles: (config: Partial<IGenerateBreakpointsStylesParams> = {}): string => {
		const {currentClassName, breakpoints, generateStyleFn, selector} = config;

		if (!breakpoints || ! generateStyleFn || ! selector) {
			return;
		}

		return Object.keys(breakpoints)
			.sort((a, b) => parseInt(a) - parseInt(b))
			.map((mediaMinWidth) => {
				let style = stylesFunctions[generateStyleFn]?.(breakpoints[mediaMinWidth]);

				if (style) {
					if (transformModifiers.has(generateStyleFn)) {
						style = mergeTransform(`${currentClassName}_${mediaMinWidth}`, style);
					}

					return `@media screen and (min-width: ${mediaMinWidth}px) {${selector} {${style}}}`;
				}

				return '';
			})
			.join(' ');
	}
};

/**
 * Builds the CSS rule for a single carousel config slice.
 *
 * Pure with respect to React: it only generates the rule, registering it in the
 * SSR style pool is up to the caller (see `useCarouselSSRStyle`).
 *
 * @returns The hashed class name and its CSS rule, or `null` when the config produces no style.
 */
export const processStyles = (
	{configKey, generateStyleFn, selector}: IProcessStylesConfig,
): ISsrStyleEntry | null => {
	const currentClassName = `cr-${stringToHash(JSON.stringify(configKey))}`;

	selector = selector.replace('{{currentClassName}}', currentClassName);

	let style = '';
	let generatedStyle = stylesFunctions[generateStyleFn](configKey);

	if (generatedStyle) {
		if (transformModifiers.has(generateStyleFn)) {
			generatedStyle = mergeTransform(currentClassName, generatedStyle);
		}

		style = `${selector} {${generatedStyle}}\n`;
	}

	if (configKey.breakpoints) {
		const breakpointsGeneratedStyle = stylesFunctions.generateBreakpointsStyles({
			currentClassName,
			breakpoints: configKey.breakpoints,
			generateStyleFn,
			selector
		});

		if (breakpointsGeneratedStyle) {
			style += `${breakpointsGeneratedStyle}\n`;
		}
	}

	if (!style) {
		return null;
	}

	return {className: currentClassName, style};
};

const mergeTransform = (className: string, newTransform: string): string => {
	const parseTranslate = (
		value: string,
		fn: 'translateX' | 'translateY' | 'translateZ'
	): string => {
		const startIndex = value.indexOf(`${fn}(`);

		if (startIndex === -1) {
			return '0px';
		}

		let openParens = 0;
		let endIndex = startIndex + fn.length + 1;

		for (; endIndex < value.length; endIndex++) {
			const char = value[endIndex];

			if (char === '(') {
				openParens++;
			} else if (char === ')') {
				if (openParens === 0) {
					break;
				}

				openParens--;
			}
		}

		return value.slice(startIndex + fn.length + 1, endIndex);
	};

	const cleanedTransform = newTransform.replace(/^\s*transform:\s*/, '').trim();
	const saved = transformObjects[className] || '';

	if (!cleanedTransform) {
		const defaultX = parseTranslate(saved, 'translateX');
		const defaultY = parseTranslate(saved, 'translateY');
		const defaultZ = parseTranslate(saved, 'translateZ');

		const fullTransform = `transform: translateX(${defaultX}) translateY(${defaultY}) translateZ(${defaultZ});`;

		transformObjects[className] = fullTransform;

		return fullTransform;
	}

	const hasX = cleanedTransform.includes('translateX');
	const hasY = cleanedTransform.includes('translateY');
	const hasZ = cleanedTransform.includes('translateZ');

	const x = hasX ? parseTranslate(cleanedTransform, 'translateX') : parseTranslate(saved, 'translateX');
	const y = hasY ? parseTranslate(cleanedTransform, 'translateY') : parseTranslate(saved, 'translateY');
	const z = hasZ ? parseTranslate(cleanedTransform, 'translateZ') : parseTranslate(saved, 'translateZ');

	const fullTransform = `transform: translateX(${x}) translateY(${y}) translateZ(${z});`;

	transformObjects[className] = fullTransform;

	return fullTransform;
};
